import { appendClientDebugAction } from "@/lib/debug/client-debug";
import {
  getClientRouteFamily,
  normalizeClientNavigationHref,
  normalizeClientNavigationPath,
} from "./client-route-family";
import {
  clearRouteOwnerShadowState,
  recordRouteOwnerShadowConsumed,
  recordRouteOwnerShadowExpired,
  recordRouteOwnerShadowHistoryCommit,
  recordRouteOwnerShadowSameTargetRenderedRoute,
  recordRouteOwnerShadowTransaction,
  type RouteOwnerHistoryCommitResult,
  type RouteOwnerSameTargetRenderedRouteResult,
} from "./client-route-owner-shadow";
import type { ClientNavReason, ClientNavTransaction } from "./client-navigation-types";

const CLIENT_NAV_TRANSACTIONS_MAX = 100;
const CLIENT_DEBUG_ACTIONS_KEY = "clientDebugActions";
const CLIENT_DEBUG_COLLECTION_ENABLED_KEY = "clientDebugCollectionEnabled";
const CLIENT_DEBUG_ACTION_EVENT = "client-debug-action";
const CLIENT_DEBUG_ACTIONS_MAX = 200;
export const BOTTOM_NAV_TRANSACTION_ACTIVE_MS = 1000;
export const BOTTOM_NAV_LATE_COMMIT_IGNORE_MS = 2500;
export const MOBILE_CTA_TRANSACTION_ACTIVE_MS = 1500;
export const MOBILE_CTA_LATE_COMMIT_IGNORE_MS = 3000;
export const IOS_SWIPE_BACK_TRANSACTION_ACTIVE_MS = 1500;
export const IOS_SWIPE_BACK_LATE_COMMIT_IGNORE_MS = 3000;
export const LOCAL_URL_TAB_NAVIGATION_SOURCE = "local-url-tab-change";
export const IOS_SWIPE_BACK_NAVIGATION_SOURCE = "client-shell-ios-swipe-back";
export const ANDROID_BACK_HOME_NAVIGATION_SOURCE = "client-shell-android-back-button-home";
export const ANDROID_BACK_FALLBACK_NAVIGATION_SOURCE = "client-shell-android-back-button-fallback";
const EXERCISE_LIST_DETAIL_NAVIGATION_SOURCE = "exercise-list-exercise-detail";

export function isNativeBackRouteOwnerSource(source: string): boolean {
  return (
    source === IOS_SWIPE_BACK_NAVIGATION_SOURCE ||
    source === ANDROID_BACK_HOME_NAVIGATION_SOURCE ||
    source === ANDROID_BACK_FALLBACK_NAVIGATION_SOURCE
  );
}

type MobileRouteTransactionPolicy = {
  activeMs: number;
  lateCommitIgnoreMs: number;
  lifecycleEventPrefix: "bottom-nav" | "mobile-route";
};

type ClientDebugAction = {
  atIso: string;
  source: string;
  event: string;
  details?: Record<string, unknown>;
};

export const CLIENT_NAV_TRANSACTION_EVENT = "client-navigation-transaction";

export type ClientNavTransactionEventDetail = {
  transaction: ClientNavTransaction;
};

let transactions: ClientNavTransaction[] = [];

function getTransactionAgeMs(transaction: ClientNavTransaction, nowMs: number): number | null {
  const initiatedAtMs = Date.parse(transaction.initiatedAtIso);
  if (!Number.isFinite(initiatedAtMs)) return null;
  return Math.max(0, nowMs - initiatedAtMs);
}

function createClientNavOwnershipTransactionId(): string {
  const cryptoLike = globalThis.crypto;
  if (cryptoLike && "randomUUID" in cryptoLike) {
    return `client-nav-${cryptoLike.randomUUID()}`;
  }
  return `client-nav-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

function appendTransactionLifecycle(
  transaction: ClientNavTransaction,
  event: string,
  details?: Record<string, unknown>,
  nowMs = Date.now()
) {
  appendClientDebugAction({
    source: "client-navigation",
    event,
    details: {
      transactionId: transaction.transactionId,
      source: transaction.source,
      reason: transaction.reason,
      fromPath: transaction.fromPath,
      toPath: transaction.toPath,
      toHref: transaction.to,
      routeFamilyFrom: transaction.routeFamilyFrom,
      routeFamilyTo: transaction.routeFamilyTo,
      accepted: transaction.accepted,
      ageMs: getTransactionAgeMs(transaction, nowMs),
      consumedAtIso: transaction.consumedAtIso ?? null,
      consumedReason: transaction.consumedReason ?? null,
      expiredAtIso: transaction.expiredAtIso ?? null,
      ...details,
    },
  });
}

function isLikelyMobileViewport(): boolean {
  if (typeof window === "undefined") return true;
  if (window.matchMedia) {
    return window.matchMedia("(max-width: 1023px)").matches;
  }
  return window.innerWidth < 1024;
}

export function getMobileRouteTransactionPolicy(
  transaction: ClientNavTransaction | null
): MobileRouteTransactionPolicy | null {
  if (!transaction || transaction.accepted !== true) return null;

  if (transaction.source === "mobile-bottom-tabs" && transaction.reason === "tab") {
    return {
      activeMs: BOTTOM_NAV_TRANSACTION_ACTIVE_MS,
      lateCommitIgnoreMs: BOTTOM_NAV_LATE_COMMIT_IGNORE_MS,
      lifecycleEventPrefix: "bottom-nav",
    };
  }

  if (
    transaction.reason === "cta" &&
    transaction.source.startsWith("client-dashboard-") &&
    isLikelyMobileViewport()
  ) {
    return {
      activeMs: MOBILE_CTA_TRANSACTION_ACTIVE_MS,
      lateCommitIgnoreMs: MOBILE_CTA_LATE_COMMIT_IGNORE_MS,
      lifecycleEventPrefix: "mobile-route",
    };
  }

  if (
    transaction.reason === "cta" &&
    transaction.source === EXERCISE_LIST_DETAIL_NAVIGATION_SOURCE &&
    isLikelyMobileViewport()
  ) {
    return {
      activeMs: MOBILE_CTA_TRANSACTION_ACTIVE_MS,
      lateCommitIgnoreMs: MOBILE_CTA_LATE_COMMIT_IGNORE_MS,
      lifecycleEventPrefix: "mobile-route",
    };
  }

  if (
    (transaction.reason === "swipe-back" || transaction.reason === "native-back") &&
    isNativeBackRouteOwnerSource(transaction.source)
  ) {
    return {
      activeMs: IOS_SWIPE_BACK_TRANSACTION_ACTIVE_MS,
      lateCommitIgnoreMs: IOS_SWIPE_BACK_LATE_COMMIT_IGNORE_MS,
      lifecycleEventPrefix: "mobile-route",
    };
  }

  if (transaction.source === LOCAL_URL_TAB_NAVIGATION_SOURCE && transaction.reason === "tab") {
    return {
      activeMs: BOTTOM_NAV_TRANSACTION_ACTIVE_MS,
      lateCommitIgnoreMs: BOTTOM_NAV_LATE_COMMIT_IGNORE_MS,
      lifecycleEventPrefix: "mobile-route",
    };
  }

  return null;
}

if (typeof window !== "undefined") {
  const nextItem: ClientDebugAction = {
    atIso: new Date().toISOString(),
    source: "client-navigation-store",
    event: "client-navigation-store-initialized",
    details: {
      transactionCount: transactions.length,
      href: window.location.href,
    },
  };
  try {
    if (localStorage.getItem(CLIENT_DEBUG_COLLECTION_ENABLED_KEY) === "1") {
      const existingRaw = localStorage.getItem(CLIENT_DEBUG_ACTIONS_KEY);
      const existing = (existingRaw ? (JSON.parse(existingRaw) as ClientDebugAction[]) : []) || [];
      const next = [...existing, nextItem].slice(-CLIENT_DEBUG_ACTIONS_MAX);
      localStorage.setItem(CLIENT_DEBUG_ACTIONS_KEY, JSON.stringify(next));
    }
  } catch {
    // No-op: diagnostics must never block product flow.
  }
  window.dispatchEvent(new CustomEvent(CLIENT_DEBUG_ACTION_EVENT, { detail: nextItem }));
}

export function recordClientNavTransaction(transaction: ClientNavTransaction): void {
  transactions = [...transactions, transaction].slice(-CLIENT_NAV_TRANSACTIONS_MAX);
  recordRouteOwnerShadowTransaction(transaction);

  const policy = getMobileRouteTransactionPolicy(transaction);
  if (policy) {
    appendTransactionLifecycle(transaction, `${policy.lifecycleEventPrefix}-transaction-created`, {
      activeMs: policy.activeMs,
      lateCommitIgnoreMs: policy.lateCommitIgnoreMs,
    });
  }

  if (typeof window === "undefined") return;

  window.dispatchEvent(
    new CustomEvent<ClientNavTransactionEventDetail>(CLIENT_NAV_TRANSACTION_EVENT, {
      detail: { transaction },
    })
  );
}

export function recordClientNavOwnershipUpdate(params: {
  to: string;
  fromHref?: string | null;
  source?: string;
  reason?: ClientNavReason;
  replace?: boolean;
  scroll?: boolean;
  expectedCurrentHref?: string | null;
  expectedCurrentPath?: string | null;
  expectedPathPrefix?: string | null;
  nowMs?: number;
}): ClientNavTransaction {
  const nowMs = params.nowMs ?? Date.now();
  const normalizedToHref = normalizeClientNavigationHref(params.to);
  const fallbackFromHref =
    typeof window !== "undefined"
      ? normalizeClientNavigationHref(`${window.location.pathname}${window.location.search}`)
      : normalizedToHref;
  const normalizedFromHref = normalizeClientNavigationHref(params.fromHref ?? fallbackFromHref);
  const fromPath = normalizeClientNavigationPath(normalizedFromHref);
  const toPath = normalizeClientNavigationPath(normalizedToHref);
  const transaction: ClientNavTransaction = {
    to: normalizedToHref,
    replace: params.replace ?? true,
    scroll: params.scroll ?? false,
    source: params.source ?? LOCAL_URL_TAB_NAVIGATION_SOURCE,
    reason: params.reason ?? "tab",
    routeFamilyFrom: getClientRouteFamily(fromPath),
    routeFamilyTo: getClientRouteFamily(toPath),
    allowDuringWorkout: false,
    transactionId: createClientNavOwnershipTransactionId(),
    fromPath,
    toPath,
    expectedCurrentHref: params.expectedCurrentHref ?? normalizedFromHref,
    expectedCurrentPath:
      params.expectedCurrentPath === null
        ? undefined
        : params.expectedCurrentPath ?? normalizeClientNavigationPath(normalizedFromHref),
    expectedPathPrefix:
      params.expectedPathPrefix === null
        ? undefined
        : params.expectedPathPrefix ?? normalizeClientNavigationPath(normalizedFromHref),
    initiatedAtIso: new Date(nowMs).toISOString(),
    accepted: true,
  };

  recordClientNavTransaction(transaction);
  return transaction;
}

export function recordBottomNavTransactionPushCalled(transaction: ClientNavTransaction, nowMs = Date.now()): void {
  const policy = getMobileRouteTransactionPolicy(transaction);
  if (!policy) return;
  appendTransactionLifecycle(
    transaction,
    `${policy.lifecycleEventPrefix}-transaction-push-called`,
    {
      activeMs: policy.activeMs,
      lateCommitIgnoreMs: policy.lateCommitIgnoreMs,
    },
    nowMs
  );
}

export function markClientNavTransactionConsumed(
  transactionId: string,
  reason: string,
  nowMs = Date.now()
): ClientNavTransaction | null {
  const index = transactions.findIndex((transaction) => transaction.transactionId === transactionId);
  if (index === -1) return null;

  const existing = transactions[index];
  const consumedTransaction = existing.consumedAtIso
    ? existing
    : {
        ...existing,
        consumedAtIso: new Date(nowMs).toISOString(),
        consumedReason: reason,
      };
  transactions = [
    ...transactions.slice(0, index),
    consumedTransaction,
    ...transactions.slice(index + 1),
  ];

  const policy = getMobileRouteTransactionPolicy(consumedTransaction);
  if (policy) {
    appendTransactionLifecycle(
      consumedTransaction,
      `${policy.lifecycleEventPrefix}-transaction-consumed`,
      { consumeReason: reason },
      nowMs
    );
  }
  recordRouteOwnerShadowConsumed(consumedTransaction, reason, nowMs);

  return consumedTransaction;
}

export function markClientNavTransactionExpired(
  transactionId: string,
  reason: string,
  nowMs = Date.now()
): ClientNavTransaction | null {
  const index = transactions.findIndex((transaction) => transaction.transactionId === transactionId);
  if (index === -1) return null;

  const existing = transactions[index];
  const expiredTransaction = existing.expiredAtIso
    ? existing
    : {
        ...existing,
        expiredAtIso: new Date(nowMs).toISOString(),
      };
  transactions = [
    ...transactions.slice(0, index),
    expiredTransaction,
    ...transactions.slice(index + 1),
  ];

  const policy = getMobileRouteTransactionPolicy(expiredTransaction);
  if (policy) {
    appendTransactionLifecycle(
      expiredTransaction,
      `${policy.lifecycleEventPrefix}-transaction-expired`,
      { expireReason: reason },
      nowMs
    );
  }
  recordRouteOwnerShadowExpired(expiredTransaction, reason, nowMs);

  return expiredTransaction;
}

export function isClientNavTransactionActive(transaction: ClientNavTransaction | null, nowMs = Date.now()): boolean {
  const policy = getMobileRouteTransactionPolicy(transaction);
  if (!transaction || !policy) return false;
  if (transaction.consumedAtIso || transaction.expiredAtIso) return false;
  const ageMs = getTransactionAgeMs(transaction, nowMs);
  return ageMs !== null && ageMs <= policy.activeMs;
}

export type BottomNavHistoryCommitDecision = {
  ignore: boolean;
  reason:
    | "not-mobile-route-transaction"
    | "target-mismatch"
    | "active"
    | "consumed"
    | "expired"
    | "target-href-mismatch"
    | "late-commit"
    | "late-commit-current-owner"
    | "route-owner-machine-suppress-commit"
    | "age-missing"
    | "allowed-child-route"
    | "unowned-route-commit";
  ageMs: number | null;
  normalizedCurrentPath: string;
  normalizedTargetPath: string | null;
  normalizedTargetHref: string | null;
  normalizedTransactionHref: string | null;
  activeMs: number | null;
  lateCommitIgnoreMs: number | null;
};

function isAllowedLocalTabChildRoute(params: {
  transaction: ClientNavTransaction;
  normalizedCurrentHref: string;
  normalizedCurrentPath: string;
  normalizedTargetPath: string | null;
  normalizedTransactionHref: string | null;
}): boolean {
  if (
    params.transaction.source !== LOCAL_URL_TAB_NAVIGATION_SOURCE ||
    params.transaction.reason !== "tab"
  ) {
    return false;
  }

  return (
    params.normalizedTransactionHref === "/client/program?tab=exercises" &&
    params.normalizedCurrentHref === params.normalizedTransactionHref &&
    params.normalizedCurrentPath === "/client/program" &&
    Boolean(params.normalizedTargetPath?.startsWith("/client/exercises/"))
  );
}

export function evaluateBottomNavHistoryCommit(params: {
  transaction: ClientNavTransaction | null;
  currentPath: string;
  targetPath: string | null;
  targetHref?: string | null;
  nowMs?: number;
}): BottomNavHistoryCommitDecision {
  const nowMs = params.nowMs ?? Date.now();
  const tx = params.transaction;
  const normalizedCurrentPath = normalizeClientNavigationPath(params.currentPath);
  const normalizedCurrentHref = normalizeClientNavigationHref(params.currentPath);
  const normalizedTargetPath = params.targetPath ? normalizeClientNavigationPath(params.targetPath) : null;
  const normalizedTargetHref = params.targetHref ? normalizeClientNavigationHref(params.targetHref) : null;
  const normalizedTransactionHref = tx ? normalizeClientNavigationHref(tx.to) : null;
  const ageMs = tx ? getTransactionAgeMs(tx, nowMs) : null;
  const policy = getMobileRouteTransactionPolicy(tx);

  if (!tx || !policy) {
    return {
      ignore: false,
      reason: "not-mobile-route-transaction",
      ageMs,
      normalizedCurrentPath,
      normalizedTargetPath,
      normalizedTargetHref,
      normalizedTransactionHref,
      activeMs: null,
      lateCommitIgnoreMs: null,
    };
  }
  if (!normalizedTargetPath) {
    return {
      ignore: false,
      reason: "target-mismatch",
      ageMs,
      normalizedCurrentPath,
      normalizedTargetPath,
      normalizedTargetHref,
      normalizedTransactionHref,
      activeMs: policy.activeMs,
      lateCommitIgnoreMs: policy.lateCommitIgnoreMs,
    };
  }
  if (
    isAllowedLocalTabChildRoute({
      transaction: tx,
      normalizedCurrentHref,
      normalizedCurrentPath,
      normalizedTargetPath,
      normalizedTransactionHref,
    })
  ) {
    return {
      ignore: false,
      reason: "allowed-child-route",
      ageMs,
      normalizedCurrentPath,
      normalizedTargetPath,
      normalizedTargetHref,
      normalizedTransactionHref,
      activeMs: policy.activeMs,
      lateCommitIgnoreMs: policy.lateCommitIgnoreMs,
    };
  }
  if (
    normalizedTransactionHref?.includes("?") &&
    normalizedTargetHref &&
    normalizedTargetHref !== normalizedTransactionHref
  ) {
    return {
      ignore: true,
      reason: "target-href-mismatch",
      ageMs,
      normalizedCurrentPath,
      normalizedTargetPath,
      normalizedTargetHref,
      normalizedTransactionHref,
      activeMs: policy.activeMs,
      lateCommitIgnoreMs: policy.lateCommitIgnoreMs,
    };
  }
  if (tx.consumedAtIso && normalizedCurrentPath === tx.toPath && normalizedTargetPath !== tx.toPath) {
    return {
      ignore: true,
      reason: "unowned-route-commit",
      ageMs,
      normalizedCurrentPath,
      normalizedTargetPath,
      normalizedTargetHref,
      normalizedTransactionHref,
      activeMs: policy.activeMs,
      lateCommitIgnoreMs: policy.lateCommitIgnoreMs,
    };
  }
  if (normalizedTargetPath !== tx.toPath) {
    return {
      ignore: normalizedCurrentPath === tx.toPath,
      reason: normalizedCurrentPath === tx.toPath ? "unowned-route-commit" : "target-mismatch",
      ageMs,
      normalizedCurrentPath,
      normalizedTargetPath,
      normalizedTargetHref,
      normalizedTransactionHref,
      activeMs: policy.activeMs,
      lateCommitIgnoreMs: policy.lateCommitIgnoreMs,
    };
  }
  if (tx.consumedAtIso) {
    return {
      ignore: true,
      reason: "consumed",
      ageMs,
      normalizedCurrentPath,
      normalizedTargetPath,
      normalizedTargetHref,
      normalizedTransactionHref,
      activeMs: policy.activeMs,
      lateCommitIgnoreMs: policy.lateCommitIgnoreMs,
    };
  }
  if (tx.expiredAtIso) {
    return {
      ignore: true,
      reason: "expired",
      ageMs,
      normalizedCurrentPath,
      normalizedTargetPath,
      normalizedTargetHref,
      normalizedTransactionHref,
      activeMs: policy.activeMs,
      lateCommitIgnoreMs: policy.lateCommitIgnoreMs,
    };
  }
  if (normalizedCurrentPath === tx.toPath) {
    return {
      ignore: false,
      reason: "active",
      ageMs,
      normalizedCurrentPath,
      normalizedTargetPath,
      normalizedTargetHref,
      normalizedTransactionHref,
      activeMs: policy.activeMs,
      lateCommitIgnoreMs: policy.lateCommitIgnoreMs,
    };
  }
  if (ageMs === null) {
    return {
      ignore: false,
      reason: "age-missing",
      ageMs,
      normalizedCurrentPath,
      normalizedTargetPath,
      normalizedTargetHref,
      normalizedTransactionHref,
      activeMs: policy.activeMs,
      lateCommitIgnoreMs: policy.lateCommitIgnoreMs,
    };
  }
  if (ageMs > policy.lateCommitIgnoreMs && normalizedCurrentPath === tx.fromPath) {
    return {
      ignore: false,
      reason: "late-commit-current-owner",
      ageMs,
      normalizedCurrentPath,
      normalizedTargetPath,
      normalizedTargetHref,
      normalizedTransactionHref,
      activeMs: policy.activeMs,
      lateCommitIgnoreMs: policy.lateCommitIgnoreMs,
    };
  }
  if (ageMs > policy.lateCommitIgnoreMs) {
    return {
      ignore: true,
      reason: "late-commit",
      ageMs,
      normalizedCurrentPath,
      normalizedTargetPath,
      normalizedTargetHref,
      normalizedTransactionHref,
      activeMs: policy.activeMs,
      lateCommitIgnoreMs: policy.lateCommitIgnoreMs,
    };
  }
  return {
    ignore: false,
    reason: "active",
    ageMs,
    normalizedCurrentPath,
    normalizedTargetPath,
    normalizedTargetHref,
    normalizedTransactionHref,
    activeMs: policy.activeMs,
    lateCommitIgnoreMs: policy.lateCommitIgnoreMs,
  };
}

export function findSupersedingClientNavOwner(params: {
  transaction: ClientNavTransaction;
  currentHref: string;
}): ClientNavTransaction | null {
  const ownerIndex = transactions.findIndex(
    (candidate) => candidate.transactionId === params.transaction.transactionId
  );
  if (ownerIndex === -1) return null;

  const normalizedCurrentHref = normalizeClientNavigationHref(params.currentHref);
  const normalizedCurrentPath = normalizeClientNavigationPath(normalizedCurrentHref);
  const ownerStartedAtMs = Date.parse(params.transaction.initiatedAtIso);

  for (let index = transactions.length - 1; index > ownerIndex; index -= 1) {
    const candidate = transactions[index];
    if (candidate.accepted !== true || candidate.transactionId === params.transaction.transactionId) continue;
    if (candidate.expiredAtIso) continue;
    if (!getMobileRouteTransactionPolicy(candidate)) continue;

    const candidateStartedAtMs = Date.parse(candidate.initiatedAtIso);
    if (
      Number.isFinite(ownerStartedAtMs) &&
      Number.isFinite(candidateStartedAtMs) &&
      candidateStartedAtMs <= ownerStartedAtMs
    ) {
      continue;
    }

    const candidateHref = normalizeClientNavigationHref(candidate.to);
    const candidatePath = normalizeClientNavigationPath(candidateHref);
    const candidateOwnsCurrentHref = candidateHref.includes("?")
      ? normalizedCurrentHref === candidateHref
      : normalizedCurrentPath === candidatePath;
    if (candidateOwnsCurrentHref) return candidate;
  }

  return null;
}

export function recordBottomNavHistoryCommitIgnored(
  transaction: ClientNavTransaction,
  decision: BottomNavHistoryCommitDecision,
  details: Record<string, unknown>,
  nowMs = Date.now()
): void {
  const policy = getMobileRouteTransactionPolicy(transaction);
  const eventPrefix = policy?.lifecycleEventPrefix ?? "mobile-route";
  appendTransactionLifecycle(
    transaction,
    decision.reason === "late-commit"
      ? `${eventPrefix}-transaction-late-commit-ignored`
      : `${eventPrefix}-transaction-duplicate-ignored`,
    {
      ignoreReason: decision.reason,
      normalizedCurrentPath: decision.normalizedCurrentPath,
      normalizedTargetPath: decision.normalizedTargetPath,
      normalizedTargetHref: decision.normalizedTargetHref,
      normalizedTransactionHref: decision.normalizedTransactionHref,
      activeMs: decision.activeMs,
      lateCommitIgnoreMs: decision.lateCommitIgnoreMs,
      ...details,
    },
    nowMs
  );
}

export function recordMobileRouteHistoryCommitDecision(
  transaction: ClientNavTransaction | null,
  decision: BottomNavHistoryCommitDecision,
  details: Record<string, unknown>,
  nowMs = Date.now()
): { routeOwnerResult: RouteOwnerHistoryCommitResult; effectiveDecision: BottomNavHistoryCommitDecision } | null {
  const policy = getMobileRouteTransactionPolicy(transaction);
  if (!transaction || !policy) return null;

  const historyEvent =
    details.historyEvent === "replaceState" || details.historyEvent === "pushState"
      ? details.historyEvent
      : "pushState";
  const targetHref =
    typeof details.url === "string"
      ? details.url
      : typeof details.targetHref === "string"
        ? details.targetHref
        : decision.normalizedTargetHref ?? decision.normalizedTargetPath ?? transaction.to;
  const currentHref =
    typeof details.currentPath === "string"
      ? details.currentPath
      : decision.normalizedCurrentPath;
  const routeOwnerResult = recordRouteOwnerShadowHistoryCommit({
    transaction,
    decision,
    mode: "authority",
    historyEvent,
    targetHref,
    currentHrefBeforeCommit: currentHref,
    nowMs,
  });
  const effectiveDecision: BottomNavHistoryCommitDecision =
    routeOwnerResult.authoritySuppressed && !decision.ignore
      ? { ...decision, ignore: true, reason: "route-owner-machine-suppress-commit" }
      : decision;

  appendTransactionLifecycle(
    transaction,
    `${policy.lifecycleEventPrefix}-transaction-history-commit-decision`,
    {
      commitIgnored: effectiveDecision.ignore,
      commitDecisionReason: effectiveDecision.reason,
      normalizedCurrentPath: decision.normalizedCurrentPath,
      normalizedTargetPath: decision.normalizedTargetPath,
      normalizedTargetHref: decision.normalizedTargetHref,
      normalizedTransactionHref: decision.normalizedTransactionHref,
      activeMs: decision.activeMs,
      lateCommitIgnoreMs: decision.lateCommitIgnoreMs,
      routeOwnerAuthority: routeOwnerResult.authoritySuppressed ? "route-owner-machine" : null,
      routeOwnerDecisionKind: routeOwnerResult.decision?.kind ?? null,
      routeOwnerDecisionReason: routeOwnerResult.decision?.reason ?? null,
      routeOwnerId: routeOwnerResult.decision?.ownerId ?? null,
      routeOwnerSource: routeOwnerResult.decision?.ownerSource ?? null,
      routeOwnerHref: routeOwnerResult.decision?.ownerHref ?? null,
      ...details,
    },
    nowMs
  );

  return { routeOwnerResult, effectiveDecision };
}

export function recordClientNavSameTargetRenderedRoute(params: {
  transaction: ClientNavTransaction;
  targetHref: string;
  currentHref: string;
  renderedCurrentPath: string;
  mode?: "shadow" | "authority";
  productionReasserted?: boolean;
  productionReason?: string;
  nowMs?: number;
}): RouteOwnerSameTargetRenderedRouteResult {
  return recordRouteOwnerShadowSameTargetRenderedRoute(params);
}

export function readClientNavTransactions(limit = 20): ClientNavTransaction[] {
  if (limit <= 0) return [];
  return transactions.slice(Math.max(0, transactions.length - limit));
}

export function getLatestClientNavTransaction(): ClientNavTransaction | null {
  return transactions[transactions.length - 1] ?? null;
}

export function clearClientNavTransactions(): void {
  transactions = [];
  clearRouteOwnerShadowState();
  if (typeof window !== "undefined") {
    appendClientDebugAction({
      source: "client-navigation-store",
      event: "client-navigation-store-cleared",
      details: {
        href: window.location.href,
        pathname: window.location.pathname,
      },
    });
  }
}
